import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { Button, message, Select, Spin } from 'antd';
import { capitalize } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';
import { useParams } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import {
  CategoryEnum,
  GetFloorPlanWebQuery,
  UpdateAndSubmitAnnotationWebMutation,
} from '../../__generated__/graphql';
import { AREA_TYPES, BREAK_POINTS, ROUTES } from '../../common/constants';
import ErrorComponent from '../../components/ErrorComponent';
import Sidebar from '../../components/Sidebar';
import useRouter from '../../hooks/useRouter';
import { DetectionType } from '../../types/reportAnnotationScreen.type';
import AnnotationModal from './components/AddAnnotationAreaModal';
import AnnotationFailModal from './components/AnnotationFailModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import DetectionCard from './components/DetectionCard';
import EditDetectionModal from './components/EditDetectionModal';
import { UPDATE_AND_SUBMIT_ANNOTATION } from './graphql/mutations';
import { GET_AREA_TYPES, GET_FLOOR_PLAN_DATA } from './graphql/queries';

//placeholder for smaller screen
const Common = ({
  onSave,
  loading,
}: {
  onSave: () => void;
  loading: boolean;
}) => {
  return (
    <div className="placeholder-component-style">
      <div className="body-base medium">
        This feature is best experienced on larger screens.
      </div>
      <p>Please use a device with a screen width of 1150px or more.</p>
      <Button
        className="mt-20"
        type="primary"
        loading={loading}
        onClick={onSave}
      >
        Submit Anyway
      </Button>
    </div>
  );
};

const Annotation = () => {
  const { id } = useParams<{ id: string }>();
  const { navigate } = useRouter();
  const { width } = useWindowSize();
  const [isFailModalVisible, setIsFailModalVisible] = useState<boolean>(false);
  const transformRef = useRef(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedID, setSelectedID] = useState<string | number | null>(null);
  const [isParent, setIsParent] = useState<boolean>(false);
  const [addAnnotationModalVisible, setAddAnnotationModalVisible] =
    useState<boolean>(false);
  const [panningDisabled, setPanningDisabled] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedDetectionId, setSelectedDetectionId] = useState<
    string | number | null
  >(null);
  const [deletedDetectionIds, setDeletedDetectionIds] = useState<
    (string | number)[]
  >([]);
  const [transformState, setTransformState] = useState({
    scale: 1,
    positionX: 0,
    positionY: 0,
  });

  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const { data, loading, error } = useQuery<GetFloorPlanWebQuery>(
    GET_FLOOR_PLAN_DATA,
    {
      variables: { floorPlanId: id },
      skip: !id,
    },
  );

  useEffect(() => {
    if (transformRef.current && isImageLoaded) {
      const timer = setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { centerView } = transformRef.current as any;
        centerView(1.3, 200);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isImageLoaded]);

  const detectionRefs = useRef<Record<string | number, HTMLDivElement | null>>(
    {},
  );
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detectionToEdit, setDetectionToEdit] = useState<DetectionType | null>(
    null,
  );
  const [newParents, setNewParents] = useState<
    {
      title: string;
      parentID: string;
      parentType: string;
      bbox: (number | null)[];
    }[]
  >([]);
  const [updateAndSubmitAnnotationWeb, { loading: mutationLoading }] =
    useMutation<UpdateAndSubmitAnnotationWebMutation>(
      UPDATE_AND_SUBMIT_ANNOTATION,
    );

  // constants area types
  const { data: staticData } = useQuery(GET_AREA_TYPES);
  const staticAreaTypes = staticData?.getFloorPlanStaticDataWeb?.data || [];

  const options = staticAreaTypes.map((item) => ({
    label: capitalize(item?.title || ''),
    value: item?.title,
  }));

  // filter with area types and update detection
  const detections = data?.getFloorPlanWeb?.annotation?.detections || [];
  const [updatedDetections, setUpdatedDetections] = useState(detections);
  const existingDetectionIds = new Set(
    detections.map((detection) => detection?.childID),
  );

  const newDetections = updatedDetections.filter(
    (u) => !existingDetectionIds.has(u?.childID),
  );
  const allDetections = [
    ...detections.map((detection) => {
      const updated = updatedDetections.find(
        (updatedDetection) => updatedDetection?.childID === detection?.childID,
      );
      return updated || detection;
    }),
    ...newDetections,
  ];

  const filteredDetections = allDetections?.filter((detection) => {
    const isNotDeleted = !deletedDetectionIds?.includes(
      detection?.childID || '',
    );
    const matchesCategory =
      selectedCategories.length === 0 ||
      selectedCategories.includes((detection?.childType ?? '').toLowerCase());

    return isNotDeleted && matchesCategory;
  });
  const imageUrl =
    data?.getFloorPlanWeb?.assets?.find(
      (asset) => asset?.category === CategoryEnum.Base,
    )?.key || '';

  //  delete detection's start
  const findRelatedChildIDs = (parentID: string | number) => {
    return allDetections
      .filter((detection) => detection?.parentID === parentID)
      .map((detection) => detection?.childID)
      .filter((id): id is string | null | undefined => !!id);
  };

  const handleDelete = (childID: string | number | undefined | null) => {
    if (!childID) return;

    const parent = allDetections.some(
      (d) => d?.childID === childID && !d?.parentID,
    );
    setIsParent(parent);
    setSelectedID(childID);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    const relatedChildIDs =
      isParent && selectedID ? findRelatedChildIDs(selectedID) : [];
    setDeletedDetectionIds((prev) =>
      [...prev, selectedID!, ...relatedChildIDs].filter(
        (id): id is string | number => id !== null && id !== undefined,
      ),
    );
    setShowDeleteModal(false);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };
  //  delete detection's end
  const handleBboxClick = (id: string | number) => {
    setSelectedDetectionId(id);
    const el = detectionRefs?.current[id];
    if (el) el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  //  handle submit updated detections start
  const handleSave = async () => {
    setTransformState({
      scale: 1,
      positionX: 0,
      positionY: 0,
    });

    if (detections.length > 0) {
      const updatedChildIDs = new Set(
        updatedDetections
          .map((detection) => detection?.childID)
          .filter(Boolean),
      );
      const cleanedOriginalDetections = detections
        .filter(
          (detection) =>
            detection?.childID &&
            !deletedDetectionIds.includes(detection?.childID) &&
            !updatedChildIDs.has(detection?.childID),
        )
        .map((detection) => {
          const { __typename, ...rest } = detection || {};
          return rest;
        });
      const cleanedUpdatedDetections = updatedDetections
        .filter(
          (detection) =>
            detection?.childID &&
            !deletedDetectionIds.includes(detection?.childID),
        )
        .map((detection) => {
          const { __typename, ...rest } = detection || {};
          return rest;
        });

      const combinedDetections = [
        ...cleanedOriginalDetections,
        ...cleanedUpdatedDetections,
      ];

      const newParentTypes = updatedDetections
        .filter(
          (detection) =>
            (!detection?.parentID || detection?.parentID === null) &&
            !deletedDetectionIds?.includes(detection?.childID || '') &&
            ![
              AREA_TYPES.TOILET_SEAT,
              AREA_TYPES.BED,
              AREA_TYPES.LIVING_ROOM,
              AREA_TYPES.KITCHEN,
              AREA_TYPES.ENTRANCE,
            ].includes((detection?.title || '').toLowerCase()),
        )
        .map((detection) => ({
          parentID: detection?.childID,
          parentType: detection?.childType,
          title: detection?.title,
          bbox: detection?.bbox,
        }));

      const originalParentTypes = (
        data?.getFloorPlanWeb?.annotation?.parentTypes || []
      )
        ?.filter(
          (parent) => !deletedDetectionIds?.includes(parent?.parentID || ''),
        )
        .map((parent) => {
          const { __typename, ...rest } = parent || {};
          return rest;
        });

      const updatedData = {
        detections: combinedDetections,
        parentTypes: [...originalParentTypes, ...newParentTypes],
      };
      try {
        const response = await updateAndSubmitAnnotationWeb({
          variables: {
            data: updatedData,
            floorPlanId: id,
          },
        });

        if (
          response?.data?.updateAndSubmitAnnotationWeb?.status === 'SUCCESS'
        ) {
          navigate(ROUTES.VASTU_REPORT.replace(':id', id as string));
        } else {
          message.error('Annotation Updating Process failed');
        }
      } catch (error) {
        setIsFailModalVisible(true);
      }
    }
  };

  const handleEdit = (detection: DetectionType) => {
    setDetectionToEdit(detection);
    setEditModalVisible(true);
  };
  const selectedAreaTypeData = staticAreaTypes.find(
    (item) => item?.title === detectionToEdit?.childType,
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [allowedParents, setAllowedParent] = useState<any>([]);

  useEffect(() => {
    const parentData =
      data?.getFloorPlanWeb?.annotation?.parentTypes?.filter((data) => {
        return selectedAreaTypeData?.allowedParentElements?.includes(
          data?.parentType as string,
        );
      }) || [];
    const newParentsData = newParents?.filter((data) => {
      return selectedAreaTypeData?.allowedParentElements?.includes(
        data?.parentType as string,
      );
    });

    setAllowedParent(
      [...parentData, ...newParentsData]?.map((detection) => ({
        label: capitalize(detection?.title || '') ?? '',
        value: detection?.parentID ?? '',
      })),
    );
  }, [detectionToEdit]);
  const [hiddenDetections, setHiddenDetections] = useState<Set<string>>(
    new Set(),
  );

  const handleUpdateDetection = (updated: DetectionType) => {
    setUpdatedDetections((prev) => {
      const existingIndex = prev.findIndex(
        (detection) => detection?.childID === updated.childID,
      );
      if (existingIndex > -1) {
        const newList = [...prev];
        newList[existingIndex] = updated;
        return newList;
      } else {
        return [...prev, updated];
      }
    });
    if (updated.childID) {
      setHiddenDetections((prev) =>
        new Set(prev)?.add(updated.childID as string),
      );

      setTimeout(() => {
        setHiddenDetections((prev) => {
          const newSet = new Set(prev);
          newSet?.delete(updated.childID as string);
          return newSet;
        });
      }, 50);
    }
  };

  if (width <= BREAK_POINTS.screen1150) {
    return <Common onSave={handleSave} loading={mutationLoading} />;
  }

  const handleAddDetection = (newDetection: {
    childID: string | null | undefined;
  }) => {
    if (!updatedDetections.some((d) => d?.childID === newDetection?.childID)) {
      const updated = [...updatedDetections, newDetection];
      setUpdatedDetections(updated);
    }
  };

  const handleAddAnnotation = () => {
    setAddAnnotationModalVisible(true);
  };

  const handleModalClose = () => {
    setAddAnnotationModalVisible(false);
  };
  if (loading)
    return (
      <Spin indicator={<LoadingOutlined spin />} className="loader"></Spin>
    );
  if (error) return <ErrorComponent />;
  return (
    <div className="annotation-container full-height-width">
      <div className="image-area full-height-width" ref={viewportRef}>
        <TransformWrapper
          ref={transformRef}
          panning={{ disabled: panningDisabled }}
          wheel={{ step: 0.1 }}
          onTransformed={(ref) => {
            const { scale, positionX, positionY } = ref.state;
            setTransformState({ scale, positionX, positionY });
          }}
        >
          <TransformComponent>
            <div
              className="scroll-container"
              style={{ position: 'relative' }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDetectionId(null);
              }}
            >
              <img
                style={{ opacity: isImageLoaded ? 1 : 0 }} // Fade in effect
                src={imageUrl}
                ref={imageRef}
                alt="Floor plan"
                className="base-image"
                onLoad={() => setIsImageLoaded(true)}
              />
              {filteredDetections.map((detection, index) => {
                if (detection?.bbox) {
                  const [x, y, x2, y2] = detection.bbox;
                  if (x != null && y != null && x2 != null && y2 != null) {
                    const width = x2 - x;
                    const height = y2 - y;

                    if (!hiddenDetections?.has(detection?.childID as string))
                      return (
                        <Rnd
                          scale={transformState.scale}
                          key={detection?.childID || index}
                          default={{ x, y, width, height }}
                          bounds="parent"
                          onResizeStart={() => setPanningDisabled(true)}
                          onDragStart={() => setPanningDisabled(true)}
                          onClick={(e: Event) => {
                            e.stopPropagation();
                            handleBboxClick(detection?.childID || index);
                          }}
                          onResizeStop={(
                            _e,
                            _direction,
                            ref,
                            _delta,
                            position,
                          ) => {
                            setPanningDisabled(false);
                            const updatedDetection = {
                              ...detection,
                              bbox: [
                                position.x,
                                position.y,
                                position.x +
                                  ref.offsetWidth / transformState.scale,
                                position.y +
                                  ref.offsetHeight / transformState.scale,
                              ],
                            };
                            const existingDetection = updatedDetections?.find(
                              (det) => det?.childID === detection?.childID,
                            );
                            if (existingDetection) {
                              const updatedDetectionsList =
                                updatedDetections?.map((det) =>
                                  det?.childID === detection?.childID
                                    ? updatedDetection
                                    : det,
                                );
                              setUpdatedDetections(updatedDetectionsList);
                            } else {
                              setUpdatedDetections([
                                ...updatedDetections,
                                updatedDetection,
                              ]);
                            }
                          }}
                          onDragStop={(_e, data) => {
                            setPanningDisabled(false);
                            const width =
                              (detection?.bbox?.[2] || 0) -
                              (detection?.bbox?.[0] || 0);
                            const height =
                              (detection?.bbox?.[3] || 0) -
                              (detection?.bbox?.[1] || 0);
                            const updatedDetection = {
                              ...detection,
                              bbox: [
                                data.x,
                                data.y,
                                data.x + width,
                                data.y + height,
                              ],
                            };
                            const existingDetection = updatedDetections?.find(
                              (det) => det?.childID === detection?.childID,
                            );

                            if (existingDetection) {
                              const updatedDetectionsList =
                                updatedDetections?.map((det) =>
                                  det?.childID === detection?.childID
                                    ? updatedDetection
                                    : det,
                                );
                              setUpdatedDetections(updatedDetectionsList);
                            } else {
                              setUpdatedDetections([
                                ...updatedDetections,
                                updatedDetection,
                              ]);
                            }
                          }}
                          style={{
                            cursor:
                              selectedDetectionId ===
                              (detection?.childID || index)
                                ? 'move'
                                : 'pointer',
                            border:
                              selectedDetectionId ===
                              (detection?.childID || index)
                                ? '3px solid blue'
                                : '2px solid red',
                            backgroundColor:
                              selectedDetectionId ===
                              (detection?.childID || index)
                                ? 'rgba(0, 0, 255, 0.2)'
                                : 'rgba(255, 0, 0, 0.2)',
                            position: 'absolute',
                          }}
                        />
                      );
                  }
                }
                return null;
              })}
            </div>
          </TransformComponent>
        </TransformWrapper>
      </div>
      <Sidebar
        isAnnotationScreen
        onSave={handleSave}
        headerContent={
          <div className="sidebar-header-content">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddAnnotation}
            >
              Add New Area
            </Button>
            <AnnotationModal
              parentDetectionsData={data || ({} as GetFloorPlanWebQuery)}
              visible={addAnnotationModalVisible}
              onCloseChildModal={handleModalClose}
              onAddDetection={handleAddDetection}
              onBoxClick={handleBboxClick}
              setVisible={setAddAnnotationModalVisible}
              newParents={newParents}
              setNewParents={setNewParents}
              currentScale={transformState}
              updatedDetections={updatedDetections}
            />
            <div className="multiselect-filter-div">
              <Select
                mode="multiple"
                className="multiselect-filter"
                placeholder="Filter Areas"
                maxTagCount="responsive"
                options={options}
                onChange={(value) => setSelectedCategories(value)}
              />
            </div>
          </div>
        }
        bodyContent={
          <div>
            {filteredDetections.map((item, index) => {
              const detectionId = item?.childID || `${index}`;
              return (
                <div
                  key={detectionId}
                  ref={(el) => (detectionRefs.current[detectionId] = el)}
                  style={{
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  {item && (
                    <DetectionCard
                      filteredDetections={filteredDetections}
                      handleBboxClick={handleBboxClick}
                      selectedId={selectedDetectionId}
                      item={item}
                      onDelete={() => handleDelete(item?.childID)}
                      onEdit={() => handleEdit(item as DetectionType)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        }
        loading={mutationLoading}
      />
      <EditDetectionModal
        open={editModalVisible}
        detection={detectionToEdit}
        onClose={() => setEditModalVisible(false)}
        onUpdate={handleUpdateDetection}
        parentOptions={allowedParents}
        parentDetectionsData={data || ({} as GetFloorPlanWebQuery)}
        updatedDetections={updatedDetections}
      />
      <AnnotationFailModal
        visible={isFailModalVisible}
        onClose={() => setIsFailModalVisible(false)}
      />
      {/* annotation delete confirmation modal */}
      <DeleteConfirmModal
        open={showDeleteModal}
        isParent={isParent}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default Annotation;
